/**
 * Utility functions for debug error handling and recovery
 */

export interface DebugErrorInfo {
  message: string;
  context: string;
  timestamp: number;
  stack?: string;
  userAgent: string;
  debugState?: any;
}

/**
 * Creates a standardized debug error object
 */
export function createDebugError(
  message: string,
  context: string,
  originalError?: Error,
  debugState?: any
): DebugErrorInfo {
  return {
    message,
    context,
    timestamp: Date.now(),
    stack: originalError?.stack,
    userAgent: navigator.userAgent,
    debugState,
  };
}

/**
 * Logs debug errors to console and localStorage for debugging
 */
export function logDebugError(errorInfo: DebugErrorInfo): void {
  // Log to console for development
  console.group('🐛 Debug System Error');
  console.error('Context:', errorInfo.context);
  console.error('Message:', errorInfo.message);
  if (errorInfo.stack) {
    console.error('Stack:', errorInfo.stack);
  }
  if (errorInfo.debugState) {
    console.error('Debug State:', errorInfo.debugState);
  }
  console.groupEnd();

  // Store in localStorage for debugging
  try {
    const existingErrors = JSON.parse(localStorage.getItem('debug-system-errors') || '[]');
    existingErrors.push(errorInfo);
    
    // Keep only last 20 errors
    const recentErrors = existingErrors.slice(-20);
    localStorage.setItem('debug-system-errors', JSON.stringify(recentErrors));
  } catch (storageError) {
    console.warn('Failed to store debug error:', storageError);
  }
}

/**
 * Gets stored debug errors for debugging purposes
 */
export function getStoredDebugErrors(): DebugErrorInfo[] {
  try {
    return JSON.parse(localStorage.getItem('debug-system-errors') || '[]');
  } catch (error) {
    console.warn('Failed to retrieve stored debug errors:', error);
    return [];
  }
}

/**
 * Clears stored debug errors
 */
export function clearStoredDebugErrors(): void {
  try {
    localStorage.removeItem('debug-system-errors');
  } catch (error) {
    console.warn('Failed to clear stored debug errors:', error);
  }
}

/**
 * Checks if debug mode should be automatically disabled due to repeated errors
 */
export function shouldDisableDebugMode(): boolean {
  const errors = getStoredDebugErrors();
  const recentErrors = errors.filter(
    error => Date.now() - error.timestamp < 5 * 60 * 1000 // Last 5 minutes
  );
  
  // Disable if more than 5 errors in the last 5 minutes
  return recentErrors.length > 5;
}

/**
 * Automatically disables debug mode and shows user notification
 */
export function autoDisableDebugMode(): void {
  try {
    // Disable debug mode
    const debugSettings = JSON.parse(localStorage.getItem('ai-character-studio-debug-settings') || '{}');
    debugSettings.isEnabled = false;
    localStorage.setItem('ai-character-studio-debug-settings', JSON.stringify(debugSettings));
    
    // Log the action
    console.warn('Debug mode automatically disabled due to repeated errors');
    
    // Dispatch event for UI components
    const event = new CustomEvent('debug:auto-disabled', {
      detail: { reason: 'repeated_errors' }
    });
    window.dispatchEvent(event);
    
  } catch (error) {
    console.error('Failed to auto-disable debug mode:', error);
  }
}

/**
 * Validates debug system health and performs recovery if needed
 */
export function validateDebugSystemHealth(): {
  isHealthy: boolean;
  issues: string[];
  recommendations: string[];
} {
  const issues: string[] = [];
  const recommendations: string[] = [];
  
  // Check for hanging resolvers
  if ((window as any).__debugRequestResolver) {
    issues.push('Hanging debug request resolver detected');
    recommendations.push('Clear hanging resolvers');
  }
  
  // Check for hanging timeouts
  if ((window as any).__debugRequestTimeout) {
    issues.push('Hanging debug timeout detected');
    recommendations.push('Clear hanging timeouts');
  }
  
  // Check error frequency
  if (shouldDisableDebugMode()) {
    issues.push('High error frequency detected');
    recommendations.push('Consider disabling debug mode temporarily');
  }
  
  // Check localStorage availability
  try {
    localStorage.setItem('debug-health-check', 'test');
    localStorage.removeItem('debug-health-check');
  } catch (error) {
    issues.push('localStorage not available');
    recommendations.push('Debug settings may not persist');
  }
  
  return {
    isHealthy: issues.length === 0,
    issues,
    recommendations,
  };
}

/**
 * Performs debug system recovery operations
 */
export function performDebugRecovery(): void {
  console.log('Performing debug system recovery...');
  
  try {
    // Clear hanging resolvers
    if ((window as any).__debugRequestResolver) {
      (window as any).__debugRequestResolver = null;
    }
    
    // Clear hanging timeouts
    if ((window as any).__debugRequestTimeout) {
      clearTimeout((window as any).__debugRequestTimeout);
      (window as any).__debugRequestTimeout = null;
    }
    
    // Dispatch recovery event
    const event = new CustomEvent('debug:recovery-performed');
    window.dispatchEvent(event);
    
    console.log('Debug system recovery completed');
  } catch (error) {
    console.error('Debug system recovery failed:', error);
  }
}

/**
 * Gets user-friendly error messages for common debug errors
 */
export function getDebugErrorMessage(error: Error | string): {
  title: string;
  message: string;
  canRetry: boolean;
} {
  const errorMessage = typeof error === 'string' ? error : error.message;
  const lowerMessage = errorMessage.toLowerCase();
  
  if (lowerMessage.includes('timeout')) {
    return {
      title: 'Debug Timeout',
      message: 'The debug interface timed out. The request will continue normally.',
      canRetry: true,
    };
  }
  
  if (lowerMessage.includes('cancelled')) {
    return {
      title: 'Request Cancelled',
      message: 'The debug request was cancelled by the user.',
      canRetry: false,
    };
  }
  
  if (lowerMessage.includes('modal') || lowerMessage.includes('dialog')) {
    return {
      title: 'Debug Interface Error',
      message: 'The debug interface encountered an error. Requests will continue normally.',
      canRetry: true,
    };
  }
  
  if (lowerMessage.includes('queue')) {
    return {
      title: 'Debug Queue Error',
      message: 'The debug request queue encountered an error. Some debug features may not work.',
      canRetry: true,
    };
  }
  
  return {
    title: 'Debug System Error',
    message: 'The debug system encountered an unexpected error. AI requests will continue normally.',
    canRetry: true,
  };
}